import {
  Account,
  Phone,
  useCustomMutation,
  useRedeemAssetShareLink,
  useSendAuthCode,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { string, object as yupObject } from 'yup';
import { PhoneValidator } from '../forms/controlledPhoneInput';

type useAcceptInvitationFormProps = {
  /** The grant is a workspace invitation. Exactly one of the two is given. */
  invitationId?: string;
  /**
   * The grant is an asset share link. Redeemed through `redeemShareLink`,
   * which hands out a fixed Restricted role with the one asset assigned — so
   * this path must never reach `acceptInvitation`, whose role selection
   * falls through to Workspace Admin. That includes `registerWithAuthCode`
   * for a brand-new account: it is called WITHOUT an invitation id, because
   * with one it would run `acceptInvitation` itself.
   */
  shareToken?: string;
  onAccept: (
    success: boolean,
    // Nullable because the form's own `null` is passed straight through on the
    // invitation path; narrowing it here would change what that flow sends.
    phone: Phone | null | undefined,
    email: string,
    reset?: () => void
  ) => void;
  account?: Account | null;
};

type ProfileFormValues = {
  firstName: string;
  lastName?: string | null;
  jobTitle?: string | null;
  email?: string | null;
  phone?: Phone | null;
};

export const useAcceptInvitationForm = (
  props: useAcceptInvitationFormProps
) => {
  const { account } = props || {};
  const formSchema = yupObject({
    firstName: string().required(),
    lastName: string().optional().nullable(),
    jobTitle: string().optional().nullable(),
    email: string().email().optional().nullable(),
    phone: PhoneValidator.optional().nullable(),
  }).test('email or phone', (value) => !!(value.phone || value.email));

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      firstName: account?.name?.first || '',
      lastName: account?.name?.last || '',
      jobTitle: account?.jobTitle || '',
      phone: account?.phone || null,
      email: account?.email || '',
    },
  });

  const { mutateAsync } = useCustomMutation({
    query: ACCEPT_INVITATION,
  });
  const { mutateAsync: redeemShareLink } = useRedeemAssetShareLink();
  const { sendAuthCodeAsync, registerWithAuthCode } = useSendAuthCode();

  const onSubmit = async (data: ProfileFormValues) => {
    if (props.shareToken) {
      await submitShare(data, props.shareToken);
      return;
    }

    const input = {
      invitationId: props.invitationId,
      name: {
        first: data.firstName,
        last: undefinedIfEmpty(data.lastName ?? ''),
      },
      jobTitle: undefinedIfEmpty(data.jobTitle ?? ''),
      phone: data.phone,
      email: data.email,
      isAuthenticated: Boolean(account),
    };

    await mutateAsync(
      { input },
      {
        onSettled: (settled) => {
          const result = (
            settled as {
              authentication?: { acceptInvitation?: { success?: boolean } };
            }
          )?.authentication?.acceptInvitation;
          props.onAccept(
            Boolean(result?.success),
            input.phone,
            input.email ?? '',
            reset
          );
        },
      }
    );
  };

  const submitShare = async (data: ProfileFormValues, token: string) => {
    const accepted = () =>
      props.onAccept(true, data.phone ?? undefined, data.email ?? '', reset);

    // Signed in: the link is redeemed for this account. A refusal throws and
    // is left to the caller, which reads the GraphQL error code off it.
    if (account) {
      await redeemShareLink({ token });
      accepted();
      return;
    }

    // Signed out: the profile becomes an account, and the redeem happens once
    // that account is signed in. `sendAuthCode` doubles as the existence
    // probe (the login screen uses it the same way): an existing account is
    // sent its code straight away, a new one is created by
    // `registerWithAuthCode`, which sends the code itself. `existed === false`
    // is the only outcome that registers, so an existing account can never be
    // re-registered.
    const contact = {
      ...(data.email && { email: data.email }),
      ...(data.phone && { phone: data.phone }),
    };
    await sendAuthCodeAsync(contact, accepted, async () => {
      await registerWithAuthCode(
        {
          name: {
            first: data.firstName,
            last: undefinedIfEmpty(data.lastName ?? ''),
          },
          jobTitle: undefinedIfEmpty(data.jobTitle ?? ''),
          ...contact,
          registerType: data.email ? 'email' : 'phone',
        },
        accepted
      );
    });
  };

  return {
    control,
    handleSubmit: handleSubmit(onSubmit),
    formState: { isValid, isDirty },
  };
};

const undefinedIfEmpty = (value: string) => {
  return value === '' ? undefined : value;
};

const ACCEPT_INVITATION = `
  mutation AcceptInvitation($input: AcceptInvitationInput!) {
    authentication {
      acceptInvitation(input: $input) {
        message
        success
      }
    }
  }
`;
